'use client';

import { faBarsSort } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { MEME_SLUG, MEME_SUBCATEGORY_SLUGS } from '@/modules/blog/constants';
import { Subcategory } from '@/types/blog.types';

interface BlogNavigationTabsProps {
  subcategories: Subcategory[];
  categorySlug: string;
  allUrl?: string;
  isPaginatedView?: boolean;
}

const sortMemeSubcategories = (subcategories: Subcategory[]): Subcategory[] => {
  const desiredOrder = Object.values(MEME_SUBCATEGORY_SLUGS);
  const orderMap = new Map(desiredOrder.map((slug, index) => [slug, index]));

  return [...subcategories].sort((a, b) => {
    const aIndex = orderMap.has(a.slug) ? orderMap.get(a.slug)! : Infinity;
    const bIndex = orderMap.has(b.slug) ? orderMap.get(b.slug)! : Infinity;
    return aIndex - bIndex;
  });
};

export const BlogNavigationTabs = ({
  subcategories,
  categorySlug,
  allUrl,
  isPaginatedView = false,
}: BlogNavigationTabsProps) => {
  const { session } = useSession();
  const isUserLoggedIn = session?.user;
  const sortedSubcategories =
    categorySlug === MEME_SLUG ? sortMemeSubcategories(subcategories) : subcategories;

  const [showSubcategories, setShowSubcategories] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(sortedSubcategories.length);
  const [showAll, setShowAll] = useState<boolean>(false);
  const searchParams = useSearchParams();

  const pathName = usePathname();
  const subCategoryPath = pathName && pathName.split('/');
  const secondPath = subCategoryPath ? subCategoryPath[2] : undefined;
  const activeTab =
    secondPath === 'all' || (secondPath === 'favorites' && isUserLoggedIn)
      ? secondPath
      : searchParams?.get('subcategory') || 'all';

  const handleToggleSubcategories = () => {
    setShowSubcategories((prev) => !prev);
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const buttonWidth = 250;
        let currentWidth = 110;
        let count = 0;

        for (const subcategory of sortedSubcategories) {
          const categoryWidth = measureTextWidth(subcategory.title) + 40;

          if (currentWidth + categoryWidth + buttonWidth > containerWidth) break;
          currentWidth += categoryWidth;
          count++;
        }

        setVisibleCount(count);
        setShowAll(count < sortedSubcategories.length);
      }
    };

    const observer = new ResizeObserver(updateVisibleCount);
    if (containerRef.current) observer.observe(containerRef.current);

    updateVisibleCount();

    return () => observer.disconnect();
  }, [sortedSubcategories]);

  const measureTextWidth = (text: string) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (context) {
      context.font = '500 16px Inter';
      return context.measureText(text).width;
    }

    return 0;
  };

  return (
    <>
      {isUserLoggedIn ? (
        <div className='flex w-full px-2 sm:px-8 items-center border-b border-gray-200'>
          <CustomLink
            className={clsx(
              'flex gap-2 p-4 items-center font-semibold text-black text-sm sm:text-base',
              activeTab === 'favorites' && 'border-b-2 border-experiment text-experiment'
            )}
            href={`/${categorySlug}/favorites`}
          >
            <FontAwesomeIcon
              icon={activeTab === 'favorites' ? faHeartSolid : faHeartRegular}
              size='lg'
              className='text-base text-experiment'
            />
            My favorite articles
          </CustomLink>
        </div>
      ) : null}
      <div
        ref={containerRef}
        className='flex w-full flex-row justify-between sm:border-b sm:border-gray-200'
      >
        <div className='hidden items-center bg-white px-8 text-sm font-medium sm:flex sm:flex-wrap sm:text-base'>
          {categorySlug !== MEME_SLUG && (
            <CustomLink
              className={clsx(
                'mr-2 text-black p-4',
                activeTab === 'all' && 'border-b-2 border-experiment text-experiment'
              )}
              href={allUrl || `/${categorySlug}/all`}
            >
              All
            </CustomLink>
          )}
          {sortedSubcategories.slice(0, visibleCount).map((link) => (
            <CustomLink
              key={link.uuid}
              className={clsx(
                'mr-2 text-black p-4',
                link.slug === activeTab && 'border-b-2 border-experiment text-experiment'
              )}
              href={`/${categorySlug}/1?subcategory=${link.slug}`}
            >
              {link.title}
            </CustomLink>
          ))}
        </div>
        {showAll ? (
          <div
            className={clsx('hidden cursor-pointer items-center px-6 py-3 sm:flex sm:pl-0')}
            onClick={handleToggleSubcategories}
          >
            <FontAwesomeIcon icon={faBarsSort} size='lg' />
            <p className='min-w-[200px] pl-2 text-sm font-medium text-gray-500 sm:text-base'>
              {showSubcategories ? 'Hide all subcategories' : 'Show all subcategories'}
            </p>
          </div>
        ) : null}
        <div
          className={clsx(
            'flex cursor-pointer items-center px-6 py-3 sm:hidden',
            isPaginatedView && 'pb-1'
          )}
          onClick={handleToggleSubcategories}
        >
          <FontAwesomeIcon icon={faBarsSort} size='lg' />
          <p className='min-w-[200px] pl-2 text-sm font-medium text-gray-500 sm:text-base'>
            {showSubcategories ? 'Hide all subcategories' : 'Show all subcategories'}
          </p>
        </div>
      </div>
      {showSubcategories ? (
        <div className='grid grid-cols-2 gap-5 border-t border-gray-200 bg-white px-6 py-4 text-sm sm:flex sm:flex-wrap sm:gap-10 sm:px-[84px] sm:text-base'>
          {categorySlug !== MEME_SLUG && (
            <div>
              <CustomLink
                className='font-medium hover:text-primary-700'
                href={allUrl || `/${categorySlug}/all`}
              >
                All
              </CustomLink>
            </div>
          )}
          {sortedSubcategories.map((subcategory) => (
            <div key={`subcategory-tab-${subcategory.uuid}`}>
              <CustomLink
                href={`/${categorySlug}/1/?subcategory=${subcategory.slug}`}
                className='font-medium hover:text-primary-700'
              >
                {subcategory.title}
              </CustomLink>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
};
